// eslint-disable-next-line no-unused-vars
const Types = require('../../../../jsdoc-types');
const { getCartFromLocalStorage } = require('../cart/localCart');
const { onOnce } = require('../common/utils/domEvents');
const { analyticsEventCenter } = require('./events');

/*
 * More info about Global-e Client Analytics and the function `gle`
 * https://docs.global-e.com/enterprise/en/client-analytics.html#oncheckoutsteploaded-203102
 */

/**
 * Global-e Order Transaction Analytics Object contains a breakdown of general
 * ordering information and transactions.
 * @typedef {object} OrderTransactionAnalyticsObject
 * @property {string} OrderID Global‑e order unique identifier
 * @property {boolean} ReplacementOrder Indicates whether the order is a replacement.
 * @property {string} CustomerCurrencyCode 3-char ISO currency code denoting the customer’s currency.
 * @property {string} MerchantCurrencyCode 3-char ISO currency code denoting the original (merchant) currency on the merchant’s side.
 * @property {boolean} IsEU Whether the destination country is in the EU.
 * @property {string} HubCountryCode Two-letter country code of the hub from which the order is dispatched to the customer.
 * @property {string} Region Customer state or region (if available).
 * @property {string} SiteURL Merchant website or store URL.
 * @property {string} DutiesGuaranteed ndicates if the customer has selected the “guaranteed duties and taxes” option.
 * @property {string} ShippingMethodName The name of the carrier, as was defined by Global-e for the specific merchant.
 * @property {string} ShippingMethodType The type of carrier, as selected by the customer on the checkout page.
 * @property {number} DeliveryDaysRangeFrom The minimum number of days to delivery, as displayed on the Checkout page.
 * @property {number} DeliveryDaysRangeTo The maximum number of days to delivery, as displayed on the Checkout page.
 * @property {string} cultureCode Culture code (language). If specified, the textual properties will be returned in the requested culture’s language if available. Texts in English will be returned by default
 * @property {string} InitialCheckoutCultureCode Culture code determined during the initial checkout load
 * @property {string} CheckoutStep The checkout step (LOAD or CONFIRMATION)
 * @property {string} CountryCode Order destination country code 2-char ISO.
 * @property {object} CustomerDetails Disabled by default
 * @property {string} CustomerDetails.MerchantUserId
 * @property {object} CustomerDetails.ShippingAddress
 * @property {string} CustomerDetails.ShippingAddress.ShippingFirstName
 * @property {string} CustomerDetails.ShippingAddress.ShippingLastName
 * @property {string} CustomerDetails.ShippingAddress.ShippingAddress1
 * @property {string} CustomerDetails.ShippingAddress.ShippingAddress2
 * @property {string} CustomerDetails.ShippingAddress.ShippingCity
 * @property {string} CustomerDetails.ShippingAddress.ShippingCountryName
 * @property {string} CustomerDetails.ShippingAddress.ShippingCountryCode
 * @property {string} CustomerDetails.ShippingAddress.ShippingState
 * @property {string} CustomerDetails.ShippingAddress.ShippingStateCode
 * @property {string} CustomerDetails.ShippingAddress.ShippingCompany
 * @property {string} CustomerDetails.ShippingAddress.ShippingZipCode
 * @property {string} CustomerDetails.ShippingAddress.ShippingPhoneNumber
 * @property {object} CustomerDetails.BillingAddress
 * @property {string} CustomerDetails.BillingAddress.BillingFirstName
 * @property {string} CustomerDetails.BillingAddress.BillingLastName
 * @property {string} CustomerDetails.BillingAddress.BillingAddress1
 * @property {string} CustomerDetails.BillingAddress.BillingAddress2
 * @property {string} CustomerDetails.BillingAddress.BillingCity
 * @property {string} CustomerDetails.BillingAddress.BillingCountryName
 * @property {string} CustomerDetails.BillingAddress.BillingCountryCode
 * @property {string} CustomerDetails.BillingAddress.BillingState
 * @property {string} CustomerDetails.BillingAddress.BillingStateCode
 * @property {string} CustomerDetails.BillingAddress.BillingCompany
 * @property {string} CustomerDetails.BillingAddress.BillingZipCode
 * @property {string} CustomerDetails.BillingAddress.BillingPhoneNumber
 * @property {string} CustomerDetails.BillingAddress.BillingEmail
 * @property {object} OrderPrices
 * @property {object} OrderPrices.MerchantTransaction
 * @property {number} OrderPrices.MerchantTransaction.EstimatedTotalPrice Estimated total order price after deducting the shipping and duties discounts.
 * @property {number} OrderPrices.MerchantTransaction.TotalProductsPrice The total price of the products (only) before discounts
 * @property {number} OrderPrices.MerchantTransaction.TotalDiscountedProductsPrice The total price of the products after product discounts
 * @property {number} OrderPrices.MerchantTransaction.EstimatedShippingCost The estimated shipping cost to be subsidized by the merchant
 * @property {number} OrderPrices.MerchantTransaction.ShippingVATRate Shipping service fee VAT rate, if applicable
 * @property {number} OrderPrices.MerchantTransaction.DutiesAndTaxes Duties subsidy discount
 * @property {number} OrderPrices.MerchantTransaction.ProductTotalVAT The VAT paid by Global-e to the merchant on the products, if applicable.
 * @property {number} OrderPrices.MerchantTransaction.TotalVAT The total VAT sum to be paid by Global-e to the Merchant on the order, if applicable.
 * @property {number} OrderPrices.MerchantTransaction.RemoteAreaSurchargeFee Merchant Remote Area Surcharge Subsidy
 * @property {object} OrderPrices.MerchantTransactionExcludingLocalVAT
 * @property {number} OrderPrices.MerchantTransactionExcludingLocalVAT.EstimatedTotalPriceExcludingVAT Estimated total order price after deducting the discounts
 * @property {number} OrderPrices.MerchantTransactionExcludingLocalVAT.TotalProductsPriceExcludingVAT Total products price before discounts (products only)
 * @property {number} OrderPrices.MerchantTransactionExcludingLocalVAT.TotalDiscountedProductsPriceExcludingVAT Total products price after discounts
 * @property {number} OrderPrices.MerchantTransactionExcludingLocalVAT.EstimatedShippingCostExcludingVAT The estimated shipping cost to be subsidised by the merchant
 * @property {number} OrderPrices.MerchantTransactionExcludingLocalVAT.CustomerShippingPriceExcludingVAT The shipping price paid by the customer
 * @property {number} OrderPrices.MerchantTransactionExcludingLocalVAT.DutiesAndTaxesExcludingVAT Duties subsidy discount taken by the Merchant
 * @property {number} OrderPrices.MerchantTransactionExcludingLocalVAT.RemoteAreaSurchargeFeeExcludingVAT Merchant Remote Area Surcharge Subsidy
 * @property {object} OrderPrices.CustomerTransaction
 * @property {number} OrderPrices.CustomerTransaction.CustomerTotalPrice The total price paid for the order by the customer
 * @property {number} OrderPrices.CustomerTransaction.CustomerTotalProductsPrice The total price paid by the customer for the products (only)
 * @property {number} OrderPrices.CustomerTransaction.CustomerTotalDiscountedProductPrice The total price of the products after deducting cart and product discounts paid by the customer
 * @property {number} OrderPrices.CustomerTransaction.CustomerShippingPrice The total shipping price after the discount is paid by the customer.
 * @property {number} OrderPrices.CustomerTransaction.CustomerDutiesAndTaxes The duties paid by the customer, including import duties, taxes, and customs clearance fees [CCF]). (Only relevant to international transactions outside the EU).
 * @property {number} OrderPrices.CustomerTransaction.CustomerTotalVAT The total VAT included in the products or shipping sum paid by the customer (EU to EU transactions), if applicable.
 * @property {number} OrderPrices.CustomerTransaction.CustomerShippingVATRate Shipping VAT rate, if applicable.
 * @property {number} OrderPrices.CustomerTransaction.CustomerUSSalesTax US sales tax paid by the customer (US customers only, as applicable)
 * @property {object} OrderPrices.CustomerTransactionInMerchantCurrency
 * @property {number} OrderPrices.CustomerTransactionInMerchantCurrency.CustomerTotalPriceInMerchantCurrency The total price paid for the order by the customer
 * @property {number} OrderPrices.CustomerTransactionInMerchantCurrency.CustomerTotalProductsPriceInMerchantCurrency The total price paid by the customer for the products (only)
 * @property {number} OrderPrices.CustomerTransactionInMerchantCurrency.CustomerTotalDiscountedProductsPriceInMerchantCurrency The total price of the products after deducting cart and product discounts paid by the customer
 * @property {number} OrderPrices.CustomerTransactionInMerchantCurrency.CustomerShippingPriceInMerchantCurrency The total shipping price, after discount, paid by the customer
 * @property {number} OrderPrices.CustomerTransactionInMerchantCurrency.CustomerVATInMerchantCurrency The total VAT sum paid by the customer
 * @property {number} OrderPrices.CustomerTransactionInMerchantCurrency.CustomerShippingVATRate Shipping VAT rate
 * @property {number} OrderPrices.CustomerTransactionInMerchantCurrency.CustomerDutiesAndTaxesInMerchantCurrency The duties paid by the customer. This includes import duties, taxes, and customs clearance fees [CCF]). (Only relevant to international transactions outside the EU).
 * @property {number} OrderPrices.CustomerTransactionInMerchantCurrency.CustomerUSSalesTaxInMerchantCurrency US sales tax paid by the customer in Merchant currency
 * @property {ProductInformation[]} ProductInformation
 * @property {object[]} PaymentMethods DEPRECATED
 * @property {string} PaymentMethods[].PaymentMethodName
 * @property {string} PaymentMethods[].PaymentMethodCode
 * @property {string} PaymentMethods[].PaymentMethodTypeCode
 * @property {string} PaymentMethods[].PaymentMethodTypeName
 * @property {object[]} OrderPaymentMethods
 * @property {number} OrderPaymentMethods[].PaymentMethodId The ID of the payment method in the Global‑e system.
 * @property {string} OrderPaymentMethods[].PaymentMethodName The name of the payment method.
 * @property {boolean} OrderPaymentMethods[].IsGiftCard Used by the merchant to identify the gift card payment method.
 * @property {string} OrderPaymentMethods[].GiftCardFields Key-values pairs from the gift card merchant app setting (in the Global‑e system). Null for regular payments (without gift cards).
 * @property {number} OrderPaymentMethods[].PaidAmountInCustomertCurrency The amount paid in customer currency.
 * @property {number} OrderPaymentMethods[].PaidIAmountnMerchantCurrency The amount paid in merchant currency, using spot conversion.
 * @property {number} OrderPaymentMethods[].PaymentMethodTypeCode Payment Method Type ID associated with the payment method type [0,5]
 * @property {string} OrderPaymentMethods[].PaymentMethodTypeName Payment Method Type Name
 * @property {object[]} Discounts
 * @property {string} Discounts[].Description The discount description
 * @property {string} Discounts[].DiscountCode The discount code
 * @property {number} Discounts[].DiscountTypeId The cart discount type. Discount related to the amount, coupon, or another promotion value.
 * @property {string} Discounts[].DiscountTypeName Discount type name, such as shipping discount, cart discount, product discount, and more.
 * @property {string} Discounts[].CouponCode The coupon code
 * @property {string} Discounts[].ProductCartItemId Cart Item Id. The connection between the discount and product by cartItemId in product - this is null if the discount does not refer to a specific product but was cart level (distributed between products)
 * @property {object} Discounts[].DiscountPrices
 * @property {object} Discounts[].DiscountPrices.MerchantTransaction
 * @property {number} Discounts[].DiscountPrices.MerchantTransaction.Price Discount Value
 * @property {number} Discounts[].DiscountPrices.MerchantTransaction.vatRate The merchant Local VAT rate
 * @property {object} Discounts[].DiscountPrices.MerchantTransactionExcludingLocalVAT
 * @property {number} Discounts[].DiscountPrices.MerchantTransactionExcludingLocalVAT.PriceExcludingVAT Discount Value
 * @property {object} Discounts[].DiscountPrices.CustomerTransaction
 * @property {number} Discounts[].DiscountPrices.CustomerTransaction.CustomerPrice Discount Value
 * @property {number} Discounts[].DiscountPrices.CustomerTransaction.CustomerVATRate VAT Amount
 * @property {object} Discounts[].DiscountPrices.CustomerTransactionInMerchantCurrency
 * @property {number} Discounts[].DiscountPrices.CustomerTransactionInMerchantCurrency.customerPriceInMerchantCurrency The price of the customer discount in customer currency
 * @property {number} Discounts[].DiscountPrices.CustomerTransactionInMerchantCurrency.customerVATRate VAT amount
 * @link https://docs.global-e.com/enterprise/en/client-analytics.html#order-transaction-analytics-object-overview
 */

/**
 * @typedef {object} ProductInformation
 * @property {string} ProductName The name of the product
 * @property {string} ProductURL URL of the product
 * @property {object[]} Categories
 * @property {string} Categories[].code Product category code
 * @property {string} Categories[].name Product category name
 * @property {object[]} Brand
 * @property {string} Brand[].code Product brand code
 * @property {string} Brand[].name Product brand name
 * @property {string} SKU Product SKU
 * @property {string} ProductGroupCode Product Group Code
 * @property {string} SecondaryProductCode Secondary Product Code
 * @property {string} Quantity Quantity of units
 * @property {string} CartItemId Cart Item ID
 * @property {string[]} ProductCouponCodes Product coupon code
 * @property {string} ParentCartItemID Parent Cart Item ID for product bundles
 * @property {string} CartItemOptionId Option Cart Item ID
 * @property {object} MetaData Metadata consists of product attributes that can vary for the same SKU; for example, customization such as engraving text.
 * @property {object[]} ProductAttributes
 * @property {object[]} ProductAttributes[].AttributeCode Attribute Code (ex: 'UN', '11108'...)
 * @property {object[]} ProductAttributes[].AttributeName Attribute Name (ex: 'UN', 'YELLOW'...)
 * @property {object[]} ProductAttributes[].AttributeTypeCode Attribute Type Code (ex: 'size', 'color'...)
 * @property {object} ProductPrices
 * @property {object} ProductPrices.MerchantTransaction
 * @property {number} ProductPrices.MerchantTransaction.ListPrice Product list price (before any discounts).
 * @property {number} ProductPrices.MerchantTransaction.SalePrice Product sale price, before applying any price modifications.
 * @property {number} ProductPrices.MerchantTransaction.TotalPrice The total price of all units
 * @property {number} ProductPrices.MerchantTransaction.VATRate The VAT rate for the product, if applicable
 * @property {number} ProductPrices.MerchantTransaction.DiscountedPrice The price after discount
 * @property {object} ProductPrices.MerchantTransactionExcludingLocalVAT
 * @property {number} ProductPrices.MerchantTransactionExcludingLocalVAT.ListPriceExcludingVAT Product list price (before discounts), before applying any price modifications.
 * @property {number} ProductPrices.MerchantTransactionExcludingLocalVAT.SalePriceExcludingVAT The price of the sale
 * @property {number} ProductPrices.MerchantTransactionExcludingLocalVAT.TotalPriceExcludingVAT Total price of all units
 * @property {number} ProductPrices.MerchantTransactionExcludingLocalVAT.DiscountedPriceExcludingVAT Discounted price
 * @property {object} ProductPrices.CustomerTransaction
 * @property {number} ProductPrices.CustomerTransaction.CustomerListPrice Product list price
 * @property {number} ProductPrices.CustomerTransaction.CustomerSalePrice Product selling price
 * @property {number} ProductPrices.CustomerTransaction.CustomerTotalPrice Total price of all units
 * @property {number} ProductPrices.CustomerTransaction.CustomerVATRate VAT rate (if applicable)
 * @property {number} ProductPrices.CustomerTransaction.CustomerDiscountedPrice The discounted price paid by the customer
 * @property {object} ProductPrices.CustomerTransactionInMerchantCurrency
 * @property {number} ProductPrices.CustomerTransactionInMerchantCurrency.CustomerListPriceInMerchantCurrency Product list price converted to merchant currency
 * @property {number} ProductPrices.CustomerTransactionInMerchantCurrency.CustomerSalePriceInMerchantCurrency Product sale price as visible to the customer, after applying country coefficient, FX conversion, rounding rule (if applicable), and IncludeVAT handling.
 * @property {number} ProductPrices.CustomerTransactionInMerchantCurrency.CustomerTotalPriceInMerchantCurrency Total price of all the units included in the order converted to merchant currency
 * @property {number} ProductPrices.CustomerTransactionInMerchantCurrency.CustomerDiscountedPriceInMerchantCurrency The reduced price (after discounts) of the product in merchant currency.
 */

/**
 * Complete the order with data that it is only available in local cart.
 * @param {Types.Order} order The order model parsed from Globale data
 * @returns {Types.Order} The order model with all needed data
 */
function fillOrderWithLocalCart(order) {
  /*
    Event data to fill    ProductLineItem property
    -------------------   ------------------------
    item_category         parentCategoryName
    item_category2        bylSubcategoryName
    item_variant          variationAttributes (color)
    product_size          variationAttributes (size)
  */
  const productLineItems = getCartFromLocalStorage()?.items ?? [];
  const result = {
    ...order,
  };
  result.items.items = result.items.items.map((item) => {
    const localItem = productLineItems.find((current) => current.id === item.id);
    return {
      ...item,
      parentCategoryName: localItem.parentCategoryName,
      bylSubcategoryName: localItem.bylSubcategoryName,
      variationAttributes: localItem.variationAttributes,
    };
  });
  return result;
}

/**
 * Parse a Global-e Order Transaction Analytics Object into an structure
 * understable for GA4
 * @param {OrderTransactionAnalyticsObject} otao The Global-e Order Transaction
 * Analytics Object
 * @returns {Types.Order} The order model for analyticsEventCenter
 */
function parseOrder(otao) {
  /** @type {Types.Order} */
  const order = {};

  order.items = {
    items: otao.ProductInformation.map(
      (productInfo) => {
        /** @type {Types.ProductLineItem} */
        const pli = {};
        pli.id = productInfo.SKU;
        pli.parentCategoryName = productInfo.Categories?.find((item) => !!item.name)?.name;
        pli.price = {
          discount: productInfo.ProductPrices.CustomerTransaction.CustomerDiscountedPrice,
          list: {
            decimalPrice: productInfo.ProductPrices.CustomerTransaction.CustomerListPrice,
          },
          sales: {
            decimalPrice: productInfo.ProductPrices.CustomerTransaction.CustomerSalePrice,
          },
        };
        pli.productName = productInfo.ProductName;
        pli.quantity = +productInfo.Quantity;
        pli.variationAttributes = productInfo.ProductAttributes.map(
          (attr) => ({
            attributeId: attr.AttributeTypeCode,
            displayName: attr.AttributeTypeCode,
            displayValue: attr.AttributeName,
            id: attr.AttributeCode,
            value: attr.AttributeCode,
          }),
        );

        return pli;
      },
    ),
  };

  order.billing = {
    payment: {
      selectedPaymentInstruments: [{
        name: otao.OrderPaymentMethods[0]?.PaymentMethodName,
        paymentMethod: otao.OrderPaymentMethods[0]?.PaymentMethodName,
      }],
    },
  };

  order.shipping = [{
    selectedShippingMethod: {
      displayName: otao.ShippingMethodType,
      shippingCost: otao.OrderPrices.CustomerTransaction.CustomerShippingPrice,
    },
  }];

  order.totals = {
    floatTotal: otao.OrderPrices.CustomerTransaction.CustomerTotalPrice,
    discounts: {
      couponCode: otao.Discounts?.[0]?.CouponCode,
    },
  };

  return fillOrderWithLocalCart(order);
}

function publishEventsFromGlobale(data) {
  if (data.StepId === data.Steps.LOADED) {
    // data contains product and cart information;
    analyticsEventCenter.publish(
      analyticsEventCenter.EVENTS.checkoutProcessStarted,
    );
  } else if (data.StepId === data.Steps.CONFIRMATION) {
    if (data.IsSuccess) {
      const order = parseOrder(data.details);
      analyticsEventCenter.publish(
        analyticsEventCenter.EVENTS.checkoutPurchaseConfirmed, {
          order,
        },
      );
    }
  }
}

function init() {
  onOnce(window, 'OnGlobaleClientSdkLoaded', () => {
    window.gle('OnCheckoutStepLoaded', publishEventsFromGlobale);
  });
}

module.exports = {
  init,
};
